/**
 * Google Analytics 4 (GA4) Event Tracking Utility
 *
 * Provides helper functions for tracking custom events in GA4.
 * Analytics is only loaded after user consent (GDPR/CCPA/DPDP compliant)
 */

const GA_MEASUREMENT_ID = 'G-8H3PCD3XKM';
const CONSENT_KEY = 'cookie_consent';

/**
 * Check if user has given analytics consent
 */
export const hasAnalyticsConsent = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONSENT_KEY) === 'accepted';
};

/**
 * Initialize Google Analytics (only call after user consent)
 */
export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return;

  // Don't initialize twice
  if (window.gtag) {
    console.debug('[Analytics] Already initialized');
    return;
  }

  // Create and inject the gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID);

  console.debug('[Analytics] Initialized with consent');
};

// Check if gtag is available and consent given
const isGtagAvailable = () => {
  return typeof window !== 'undefined' &&
         typeof window.gtag === 'function' &&
         hasAnalyticsConsent();
};

/**
 * Track a custom event in GA4
 * @param {string} eventName - The name of the event
 * @param {object} params - Additional parameters for the event
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) {
    console.debug('[Analytics] Skipping event (no consent or gtag unavailable):', eventName);
    return;
  }

  try {
    window.gtag('event', eventName, params);
  } catch (error) {
    console.debug('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track user sign up
 * @param {string} method - The signup method (email, google)
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method,
  });
};

/**
 * Track user login
 * @param {string} method - The login method (email, google)
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method,
  });
};

/**
 * Track when user starts a challenge
 * @param {string} challengeId - The challenge ID
 * @param {string} challengeTitle - The challenge title
 * @param {string} difficulty - The difficulty level
 */
export const trackChallengeStart = (challengeId, challengeTitle, difficulty) => {
  trackEvent('challenge_start', {
    challenge_id: challengeId,
    challenge_title: challengeTitle,
    difficulty,
  });
};

/**
 * Track when user submits a challenge
 * @param {string} challengeId - The challenge ID
 * @param {string} challengeTitle - The challenge title
 * @param {string} difficulty - The difficulty level
 * @param {number} score - The score achieved
 * @param {string} grade - The grade (S, A, B, C, D, F)
 */
export const trackChallengeSubmit = (challengeId, challengeTitle, difficulty, score, grade) => {
  trackEvent('challenge_submit', {
    challenge_id: challengeId,
    challenge_title: challengeTitle,
    difficulty,
    score,
    grade,
  });
};

/**
 * Track when user views the pricing page
 */
export const trackViewPricing = () => {
  trackEvent('view_item', {
    currency: 'INR',
    items: [{
      item_id: 'premium_annual',
      item_name: 'Premium Annual Subscription',
      item_category: 'subscription',
    }],
  });
};

/**
 * Track when user starts the checkout process
 * @param {number} price - The price in INR (not paise)
 */
export const trackBeginCheckout = (price) => {
  trackEvent('begin_checkout', {
    currency: 'INR',
    value: price,
    items: [{
      item_id: 'premium_annual',
      item_name: 'Premium Annual Subscription',
      item_category: 'subscription',
      price: price,
      quantity: 1,
    }],
  });
};

/**
 * Track successful premium purchase
 * @param {string} transactionId - The Razorpay payment ID
 * @param {number} price - The price in INR (not paise)
 */
export const trackPurchase = (transactionId, price) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    currency: 'INR',
    value: price,
    items: [{
      item_id: 'premium_annual',
      item_name: 'Premium Annual Subscription',
      item_category: 'subscription',
      price: price,
      quantity: 1,
    }],
  });
};

/**
 * Track page views (for SPA navigation)
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * Set user ID for cross-device tracking (only for logged-in users)
 * @param {string} userId - The user ID (hashed for privacy)
 */
export const setUserId = (userId) => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('set', { user_id: userId });
  } catch (error) {
    console.debug('[Analytics] Error setting user ID:', error);
  }
};

/**
 * Clear user ID on logout
 */
export const clearUserId = () => {
  if (!isGtagAvailable()) return;

  try {
    window.gtag('set', { user_id: null });
  } catch (error) {
    console.debug('[Analytics] Error clearing user ID:', error);
  }
};
